<script setup lang="ts">
import { ref } from "vue";

const {
  qrLoaded,
  qrUrl,
  qrCaptionText,
  qrHintText,
  copyRowLabel,
  copyRowValue,
  t,
} = defineProps({
  qrLoaded: { type: Boolean, default: false },
  qrUrl: { type: String, default: "" },
  qrCaptionText: { type: String, required: true },
  qrHintText: { type: String, required: true },
  copyRowLabel: { type: String, default: "" },
  copyRowValue: { type: String, default: "" },
  t: { type: Function, required: true },
});

const emit = defineEmits(["load", "attempt"]);

const copied = ref(false);

function fallbackCopy(text: string) {
  const ta = document.createElement("textarea");
  ta.value = text;
  ta.setAttribute("readonly", "true");
  ta.style.position = "fixed";
  ta.style.left = "-9999px";
  document.body.appendChild(ta);
  ta.select();

  try {
    document.execCommand("copy");
    return true;
  } catch {
    return false;
  } finally {
    document.body.removeChild(ta);
  }
}

async function copyRow() {
  if (!copyRowValue) return;

  try {
    if (navigator.clipboard?.writeText) {
      await navigator.clipboard.writeText(copyRowValue);
    } else {
      const ok = fallbackCopy(copyRowValue);
      if (!ok) return;
    }

    copied.value = true;
    emit("attempt");
    setTimeout(() => {
      copied.value = false;
    }, 1200);
  } catch {}
}

function downloadQr() {
  if (!qrUrl) return;

  emit("attempt");

  const a = document.createElement("a");
  a.href = qrUrl;
  a.download = "payment-qr.png";
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
}
</script>

<template>
  <section class="panel qr-panel">
    <div class="qr-large expanded" aria-label="QR code">
      <div class="qr-frame">
        <div class="qr-gradient-bg" aria-hidden="true"></div>
        <div v-if="!qrLoaded" class="qr-loading">
          <div class="spinner"></div>
          <span>{{ t("loadingQr") }}</span>
        </div>
        <img
            :src="qrUrl"
            alt="QR Code"
            class="qr-img"
            :class="{ hidden: !qrLoaded }"
            @load="$emit('load')"
        />
      </div>

      <p class="qr-caption">{{ qrCaptionText }}</p>
      <p class="qr-demo">{{ qrHintText }}</p>

      <div class="qr-or"><span>{{ t("or") }}</span></div>

      <button
          type="button"
          class="btn-download-qr"
          :disabled="!qrLoaded"
          @click="downloadQr"
      >
        <svg
            class="btn-download-qr-icon"
            viewBox="0 0 24 24"
            xmlns="http://www.w3.org/2000/svg"
            aria-hidden="true"
        >
          <path
              d="M12 3v10m0 0l-4-4m4 4l4-4M5 19h14"
              fill="none"
              stroke="currentColor"
              stroke-width="2"
              stroke-linecap="round"
              stroke-linejoin="round"
          />
        </svg>
        {{ t("downloadQr") }}
      </button>

      <div
          v-if="copyRowValue"
          class="qr-upi-block"
          role="group"
          :aria-label="`${copyRowLabel}: ${copyRowValue}`"
      >
        <span v-if="copyRowLabel" class="qr-upi-label">{{ copyRowLabel }}</span>

        <div class="qr-upi-row">
          <div class="qr-upi-id">{{ copyRowValue }}</div>

          <button
              type="button"
              class="btn-copy qr-upi-copy"
              :class="{ copied }"
              @click="copyRow"
          >
            {{ copied ? t("copied") : t("copy") }}
          </button>
        </div>
      </div>
    </div>
  </section>
</template>

<style scoped>
.qr-panel {
  display: flex;
  flex-direction: column;
  align-items: stretch;
}

.qr-large {
  padding: 1rem 0;
}

.qr-or {
  display: flex;
  align-items: center;
  gap: 12px;
  width: 220px;
  max-width: 80%;
  margin: 0.85rem auto 0;
  color: var(--text-muted);
  font-size: 0.8rem;
  font-weight: 600;
  letter-spacing: 0.08em;
  text-transform: uppercase;
}

.qr-or::before,
.qr-or::after {
  content: "";
  flex: 1;
  height: 1px;
  background: currentColor;
  opacity: 0.35;
}

.btn-download-qr {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  margin: 0.75rem auto 0;
  padding: 10px 20px;
  background: transparent;
  border: 1px solid var(--text-muted);
  border-radius: var(--radius-sm);
  color: var(--text);
  font-size: 0.9rem;
  font-weight: 600;
  cursor: pointer;
  transition: border-color 0.2s ease, color 0.2s ease, opacity 0.2s ease;
}

.btn-download-qr:hover {
  border-color: var(--text);
}

.btn-download-qr:disabled {
  opacity: 0.5;
  cursor: default;
}

.btn-download-qr-icon {
  width: 18px;
  height: 18px;
}
</style>
